import React from 'react';
import { useTranslation } from 'react-i18next';

interface BroadcastCardProps {
  onClick: () => void;
  isPublished: boolean;
  title?: string;
  description?: string;
  additionalInfo?: string;
  icon?: React.ReactNode;
  loading?: boolean;
}

const BroadcastCard: React.FC<BroadcastCardProps> = ({
  onClick,
  isPublished,
  loading,
  title,
  description,
  additionalInfo,
  icon,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
      {/* Governmental header */}
      <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-6 py-4 flex items-center gap-3">
        <div className="flex-shrink-0">
          {icon || (
            <svg
              className="w-8 h-8 text-amber-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
          <h3 className="text-lg font-semibold text-white">{title || t("BroadcastResearchToPublic")}</h3>
        </div>
      </div>
      {/* Body */}
      <div className="p-6">
        <div className="flex-1">
          <p className="text-gray-700 mb-3 leading-relaxed">{description || t("BroadcastResearchDescription")}</p>
          <p className="text-sm text-gray-500 mb-6 italic">{additionalInfo || t("BroadcastResearchAdditionalInfo")}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                isPublished
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {isPublished ? t("CurrentlyPublished") : t("NotPublished")}
              </span>
            </div>
            <button
              onClick={onClick}
              type="button"
              disabled={loading}
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm transition-all duration-200 ${
                isPublished
                  ? 'bg-[#9B0505] hover:bg-[#7d0404] text-white focus:ring-[#9B0505]'
                  : 'bg-[#0A1D39] hover:bg-[#13294d] text-white focus:ring-[#0A1D39]'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isPublished ? t("Unpublishing") : t("Broadcasting")}
                </>
              ) : (
                <>
                  {isPublished ? (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                      {t("Unpublish")}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t("BroadcastToPublic")}
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastCard;